/**
 * 계약관리 ID 셀
 *
 * 계약관리 ID가 단독으로 표시되는 목록(이용자, 방문자 등)에서 쓴다.
 * 계약관리 ID 자체로는 이동할 계약을 정할 수 없으므로,
 * 같은 계약관리에 묶인 계약 중 대표 계약(계약상태 '이용중' 우선, 없으면 최신 contractId)의
 * 신청/계약 상세로 새 창 이동한다.
 *
 * 이동할 계약을 못 찾아도 계약관리 ID 값 자체는 식별자이므로 링크 없이 노출한다.
 */
import { useQuery } from "@tanstack/react-query";
import { getContractApply } from "src/api/contract/contract-api";
import useApiOperation from "src/api/hooks/api-operation";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import { findRepresentativeContract } from "src/pages/contract/hooks/useContractRenewal";
import { getContractDetailPath } from "src/utils/route-util";

type Props = {
  /** 이 셀이 표기할 계약관리 ID */
  contractManageId?: number | string | null;
  /** 목록 전체 행의 계약관리 ID. 행마다 조회하지 않고 한 번에 계약 목록을 받아온다 */
  contractManageIdsInPage: Array<number | string | undefined | null>;
};

const ContractManageIdLinkCell = ({ contractManageId, contractManageIdsInPage }: Props) => {
  const { executeAsync: getContractList } = useApiOperation(getContractApply);

  //같은 계약관리에 속한 행이 여러 개면 id가 중복되므로 중복 제거 후 전달한다
  const contractManageIds = Array.from(new Set(contractManageIdsInPage.filter(Boolean).map(String))).join(",");

  //재계약이 있으면 계약관리 하나당 계약이 여러 건이므로 기본 페이지 크기로는 잘릴 수 있다
  const { data: contractList } = useQuery({
    queryKey: ["contractList", contractManageIds],
    queryFn: () => getContractList({ contractManageIds, page: 0, size: 999 }),
    enabled: !!contractManageIds,
  });

  const displayValue = contractManageId ? String(contractManageId) : "-";

  const contractId = findRepresentativeContract(
    contractList?.data?.data?.content?.filter((item) => String(item.contractManageId) === String(contractManageId)),
  )?.contractId?.toString();

  if (!contractManageId || !contractId) {
    return <div className="w-100 text-left">{displayValue}</div>;
  }

  return <BaseNewTabLink path={getContractDetailPath({ contractId })} value={displayValue} className="w-100" />;
};

export default ContractManageIdLinkCell;
